/**
 * Desktop file encryption utilities using Web Crypto API
 * Provides AES-GCM encryption with optional password protection
 *
 * - No password: Simple obfuscation (keeps internal format safe)
 * - With password: Strong encryption (AES-256-GCM + PBKDF2)
 */

const ENCRYPTION_CONFIG = {
  algorithm: "AES-GCM",
  keyLength: 256,
  ivLength: 12, // 96 bits for GCM
  saltLength: 16, // 128 bits
  iterations: 100000, // PBKDF2 iterations (only for password mode)
  tagLength: 128, // Authentication tag length
  versionEncrypted: "ENC_V1", // With password
  versionObfuscated: "OBF_V1", // Without password
};

// Static key for obfuscation mode (no password)
// This is NOT secure encryption, just prevents casual reading/editing
const OBFUSCATION_KEY_MATERIAL = new Uint8Array([
  0x4f, 0x64, 0x69, 0x6e, 0x53, 0x69, 0x67, 0x68, 0x74, 0x43, 0x68, 0x61, 0x72,
  0x74, 0x44, 0x61, 0x74, 0x61, 0x46, 0x6f, 0x72, 0x6d, 0x61, 0x74, 0x50, 0x72,
  0x6f, 0x74, 0x65, 0x63, 0x74, 0x69,
]); // "OdinSightChartDataFormatProtecti"

/**
 * Import obfuscation key (static, no password required)
 * @returns {Promise<CryptoKey>} - Static encryption key
 */
async function getObfuscationKey() {
  return crypto.subtle.importKey(
    "raw",
    OBFUSCATION_KEY_MATERIAL,
    {
      name: ENCRYPTION_CONFIG.algorithm,
      length: ENCRYPTION_CONFIG.keyLength,
    },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Derive encryption key from password using PBKDF2
 * @param {string} password - User's encryption password
 * @param {Uint8Array} salt - Random salt
 * @returns {Promise<CryptoKey>} - Derived encryption key
 */
async function deriveKey(password, salt) {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    passwordBuffer,
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  // Derive AES-GCM key using PBKDF2
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: ENCRYPTION_CONFIG.iterations,
      hash: "SHA-256",
    },
    keyMaterial,
    {
      name: ENCRYPTION_CONFIG.algorithm,
      length: ENCRYPTION_CONFIG.keyLength,
    },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt desktop data
 * @param {string} jsonData - Desktop data as JSON string
 * @param {string} password - Optional encryption password (blank = obfuscation only)
 * @returns {Promise<string>} - Encrypted data as base64 string
 */
export async function encryptDesktop(jsonData, password = "") {
  try {
    const usePassword = password && password.trim().length > 0;
    const version = usePassword
      ? ENCRYPTION_CONFIG.versionEncrypted
      : ENCRYPTION_CONFIG.versionObfuscated;

    // Generate random IV (always needed)
    const iv = crypto.getRandomValues(
      new Uint8Array(ENCRYPTION_CONFIG.ivLength),
    );

    let key;
    let salt = null;

    if (usePassword) {
      // Password mode: generate salt and derive key
      salt = crypto.getRandomValues(
        new Uint8Array(ENCRYPTION_CONFIG.saltLength),
      );
      key = await deriveKey(password.trim(), salt);
    } else {
      // Obfuscation mode: use static key, no salt
      key = await getObfuscationKey();
    }

    // Encrypt data
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonData);

    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: ENCRYPTION_CONFIG.algorithm,
        iv: iv,
        tagLength: ENCRYPTION_CONFIG.tagLength,
      },
      key,
      dataBuffer,
    );

    // Combine: version + [salt] + iv + encrypted data
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const versionBytes = new TextEncoder().encode(version);

    const totalLength =
      versionBytes.length +
      1 + // version + delimiter
      (salt ? salt.length : 0) + // salt (only if password mode)
      iv.length +
      encryptedArray.length;

    const combined = new Uint8Array(totalLength);

    let offset = 0;
    combined.set(versionBytes, offset);
    offset += versionBytes.length;
    combined[offset++] = 0x00; // Null delimiter

    if (salt) {
      combined.set(salt, offset);
      offset += salt.length;
    }

    combined.set(iv, offset);
    offset += iv.length;
    combined.set(encryptedArray, offset);

    // Convert to base64 for text storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error("Encryption failed:", error);
    throw new Error("Falha ao criptografar dados do desktop");
  }
}

/**
 * Decrypt desktop data
 * @param {string} encryptedData - Encrypted data as base64 string
 * @param {string} password - Optional decryption password (blank = obfuscation mode)
 * @returns {Promise<string>} - Decrypted JSON string
 */
export async function decryptDesktop(encryptedData, password = "") {
  try {
    // Decode base64
    const binaryString = atob(encryptedData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Extract version
    let offset = 0;
    while (offset < bytes.length && bytes[offset] !== 0x00) {
      offset++;
    }

    const versionBytes = bytes.slice(0, offset);
    const version = new TextDecoder().decode(versionBytes);
    offset++; // Skip delimiter

    const usePassword = version === ENCRYPTION_CONFIG.versionEncrypted;

    // Validate version
    if (
      version !== ENCRYPTION_CONFIG.versionEncrypted &&
      version !== ENCRYPTION_CONFIG.versionObfuscated
    ) {
      throw new Error("Versão de criptografia desconhecida");
    }

    // Check password requirement
    if (usePassword && (!password || password.trim().length === 0)) {
      throw new Error("PASSWORD_REQUIRED");
    }

    if (!usePassword && password && password.trim().length > 0) {
      throw new Error("Este arquivo foi salvo sem proteção por senha");
    }

    let key;

    if (usePassword) {
      // Password mode: extract salt and derive key
      const salt = bytes.slice(offset, offset + ENCRYPTION_CONFIG.saltLength);
      offset += ENCRYPTION_CONFIG.saltLength;
      key = await deriveKey(password.trim(), salt);
    } else {
      // Obfuscation mode: use static key
      key = await getObfuscationKey();
    }

    // Extract IV and encrypted data
    const iv = bytes.slice(offset, offset + ENCRYPTION_CONFIG.ivLength);
    offset += ENCRYPTION_CONFIG.ivLength;
    const encryptedBuffer = bytes.slice(offset);

    // Decrypt data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: ENCRYPTION_CONFIG.algorithm,
        iv: iv,
        tagLength: ENCRYPTION_CONFIG.tagLength,
      },
      key,
      encryptedBuffer,
    );

    // Convert to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error("Decryption failed:", error);

    // More specific error messages
    if (error.message === "PASSWORD_REQUIRED") {
      throw error; // Pass through as-is
    }

    if (error.name === "OperationError") {
      throw new Error("Senha inválida ou arquivo corrompido");
    }

    throw new Error("Falha ao descriptografar dados do desktop");
  }
}

/**
 * Check if data is encrypted/obfuscated
 * @param {string} data - Data to check
 * @returns {object|null} - {encrypted: boolean, passwordRequired: boolean} or null if plain
 */
export function checkEncryption(data) {
  if (!data || typeof data !== "string") return null;

  try {
    // Try to detect if it's base64-encoded
    const decoded = atob(data);

    // Check version markers
    if (decoded.startsWith(ENCRYPTION_CONFIG.versionEncrypted)) {
      return { encrypted: true, passwordRequired: true };
    }

    if (decoded.startsWith(ENCRYPTION_CONFIG.versionObfuscated)) {
      return { encrypted: true, passwordRequired: false };
    }

    return null; // Not encrypted
  } catch {
    return null; // Not base64
  }
}

/**
 * Check if data is encrypted (backward compatibility)
 * @param {string} data - Data to check
 * @returns {boolean} - True if encrypted or obfuscated
 */
export function isEncrypted(data) {
  const result = checkEncryption(data);
  return result !== null;
}

/**
 * Check if a file content is plain JSON (not encrypted)
 * @param {string} content - File content
 * @returns {boolean} - True if content is valid JSON
 */
export function isPlainJson(content) {
  try {
    JSON.parse(content);
    return true;
  } catch {
    return false;
  }
}
